import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function PartnerChat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const end = useRef(null);

  // useCallback so the identity is stable across renders — this lets us list
  // it as a useEffect dep without triggering an infinite reload loop.
  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/contacts");
      setContacts(data || []);
      setActive(prev => prev || (data?.[0]?.id ?? ""));
    } catch (err) { console.debug("chat contacts load failed:", err?.message); }
  }, []);
  const loadMsgs = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/chat/messages?peer_id=${pid}`);
      setMsgs(data || []);
    } catch (err) { console.debug("chat messages load failed:", err?.message); }
  }, []);
  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { if (active) loadMsgs(active); }, [active, loadMsgs]);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => loadMsgs(active), 6000);
    return () => clearInterval(t);
  }, [active, loadMsgs]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const text = draft.trim(); if (!text || !active) return;
    try { await api.post("/chat/messages", { peer_id: active, text }); setDraft(""); await loadMsgs(active); await loadContacts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const activeC = contacts.find(c => c.id === active);
  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" style={{ minHeight: 480 }} data-testid="partner-chat">
      <div className="aii-card p-2 md:col-span-1 flex flex-col">
        <div className="text-xs text-[color:var(--aii-muted)] p-2">Team members you can message</div>
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 460 }}>
          {contacts.length === 0 && <div className="text-xs italic text-[color:var(--aii-muted)] p-3">No contacts available yet.</div>}
          {contacts.map(c => (
            <button key={c.id} data-testid={`pchat-c-${c.id}`} onClick={() => setActive(c.id)}
                    className={`w-full text-left p-2 rounded hover:bg-[color:var(--aii-green-tint)] flex items-start justify-between ${active === c.id ? "bg-[color:var(--aii-green-tint)]" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-sm truncate">{c.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded uppercase font-bold bg-emerald-100 text-emerald-800">{c.role}</span>
                </div>
                {c.last_text && <div className="text-xs text-[color:var(--aii-muted)] truncate">{c.last_text}</div>}
              </div>
              {c.unread > 0 && <span className="inline-flex bg-brand text-white text-[10px] px-1.5 rounded-full font-bold">{c.unread}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="aii-card md:col-span-2 flex flex-col">
        {!activeC && <div className="flex-1 flex items-center justify-center text-sm text-[color:var(--aii-muted)]">Select a contact to start chatting.</div>}
        {activeC && (
          <>
            <div className="p-3 border-b border-[color:var(--aii-border)]"><b>{activeC.name}</b> <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold bg-emerald-100 text-emerald-800 ml-1">{activeC.role}</span></div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 400 }}>
              {msgs.map(m => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-brand text-white rounded-br-sm" : "bg-[color:var(--aii-bg)] rounded-bl-sm"}`}>{m.text}<div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[color:var(--aii-muted)]"}`}>{fmt(m.created_at)}</div></div>
                  </div>
                );
              })}
              <div ref={end}/>
            </div>
            <div className="p-2 border-t border-[color:var(--aii-border)] flex gap-2">
              <input data-testid="pchat-input" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="Type a message…" className="flex-1 px-3 py-2 border border-[color:var(--aii-border)] rounded text-sm"/>
              <button data-testid="pchat-send" onClick={send} disabled={!draft.trim()} className="px-3 py-2 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1"><Send size={13}/> Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
